import { useDebounceFunc } from "client/hooks/utils/useDebounceFunc";
import { useGoogleAnalytics } from "client/hooks/utils/useGoogleAnalytics";
import { get } from "client/services/Backend/get";
import { MouseEvent, UIEvent, useEffect, useRef, useState } from "react";
import { store, useAppSelector } from "store/store";
import { MemeTemplate } from "types/meme";
import { useMemeEditor } from "../../MemeEditorContext";
import { useSelectMeme } from "./useSelectMeme";
import { useRouter } from "next/router";
import translatedRoutedSlugs from "translatedRouteSlugs";

export interface MemeSearchReturn {
    memes: Array<MemeTemplate>;
    hasNextPage: boolean;
    page: number;
}

export const useMemesList = () => {
    const { initialMemes, defaultMeme } = useMemeEditor();
    const [memes, setMemes] = useState(initialMemes);
    const [isFetching, setIsFetching] = useState(false);
    const debounce = useDebounceFunc();
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isFetchingNewPage, setIsFetchingNewPage] = useState(false);
    const [search, setSearch] = useState("");
    const memesListRef = useRef<HTMLDivElement>(null);
    const { handleSelectMeme } = useSelectMeme();
    const selectedMemeId = useAppSelector((state) => state.memeGenerator.meme?.template.id);
    const { gtag: onClickGtag } = useGoogleAnalytics(true);
    const { gtag: onSearchGtag } = useGoogleAnalytics();
    const { locale, replace } = useRouter();

    function handleClickMeme(meme: MemeTemplate, e?: MouseEvent) {
        e?.preventDefault();
        store.getState().memeGenerator.meme?.template.slugKey !== meme.slugKey &&
            onClickGtag("event", "select_a_meme", {
                page: "meme generator",
                meme_name: meme.slugKey,
            });
        handleSelectMeme(meme);
        debounce(() => {
            const path = `/${translatedRoutedSlugs["/meme-generator/edit/:slug"][
                (locale as keyof (typeof translatedRoutedSlugs)["/meme-generator/edit/:slug"]) || "en"
            ].replace(":slug", meme.slugKey)}`;
            console.log({ path });
            replace(path);
        });
    }

    const loadNextPage = async () => {
        setIsFetchingNewPage(true);
        const { error, result } = await get<MemeSearchReturn>(
            `${process.env.NEXT_PUBLIC_BE_URL}/memes?page=${page + 1}&search=${search}`,
        );
        setPage(page + 1);
        setIsFetchingNewPage(false);
        if (!result || error) return;
        setHasNextPage(result.hasNextPage);
        setMemes((state) => {
            const firstChunkMemeIds = state.slice(0, 50).map((meme) => meme.id);
            return state.concat(result.memes.filter((meme) => !firstChunkMemeIds.includes(meme.id)));
        });
    };

    const handleSearch = async (search: string) => {
        setSearch(search);
        debounce(async () => {
            setIsFetching(true);
            onSearchGtag("event", "meme search", {
                page: "meme generator",
                meme_search_text: search,
            });
            const { error, result } = await get<MemeSearchReturn>(
                `${process.env.NEXT_PUBLIC_BE_URL}/memes?page=1&search=${search}`,
            );
            setPage(1);
            setIsFetching(false);
            if (!result || error) return;
            setHasNextPage(result.hasNextPage);
            setMemes(result.memes);
        });
    };

    function handleScroll(e: UIEvent) {
        const el = e.target as HTMLElement;
        if (
            !isFetchingNewPage &&
            !isFetching &&
            hasNextPage &&
            el.scrollHeight - el.clientHeight - el.scrollTop <= 200
        ) {
            loadNextPage();
        }
    }

    function handleHorizontalScroll(e: UIEvent) {
        const el = e.target as HTMLElement;
        const margin = el.scrollWidth - el.clientWidth - el.scrollLeft;
        if (!isFetchingNewPage && !isFetching && hasNextPage && margin <= 200) {
            loadNextPage();
            setTimeout(() => {
                el.scrollBy({
                    left: el.scrollWidth,
                    behavior: "smooth",
                });
            }, 250);
        }
    }

    function loadDefaultMeme() {
        const meme =
            typeof structuredClone !== "undefined"
                ? structuredClone(defaultMeme || memes[0])
                : JSON.parse(JSON.stringify(defaultMeme || memes[0]));
        meme && handleSelectMeme(meme);
    }
    useEffect(loadDefaultMeme, []);

    return {
        memes,
        isFetching,
        search,
        handleSearch,
        isFetchingNewPage,
        memesListRef,
        handleClickMeme,
        handleScroll,
        handleHorizontalScroll,
        hasNextPage,
        selectedMemeId,
    };
};
